import { computed, ref, type ComputedRef, type Ref } from 'vue'

type PreviewStageKey = 'title' | 'menu' | 'dialog' | 'replay' | 'saveLoad' | 'system' | 'payment'
type PreviewStageSize = { width: number; height: number }

type PreviewStageScaleMap = Record<PreviewStageKey, ComputedRef<number>>
type PreviewStageResizeHandlerMap = Record<PreviewStageKey, (size: PreviewStageSize) => void>

/**
 * 统一维护设置弹窗各预览画布的舞台尺寸与缩放比例。
 * @description 让组件只消费按页签划分的缩放结果与 resize 处理器，避免在组装层重复声明多组尺寸状态。
 * @param designWidth 工程设计宽度
 * @param designHeight 工程设计高度
 */
export function usePreviewStageState(
  designWidth: Ref<number>,
  designHeight: Ref<number>,
): {
  stageScales: PreviewStageScaleMap
  resizeHandlers: PreviewStageResizeHandlerMap
} {
  const titleStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const menuStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const dialogStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const replayStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const saveLoadStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const systemStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })
  const paymentStageSize = ref<PreviewStageSize>({ width: 0, height: 0 })

  const resolveStageScale = (size: PreviewStageSize): number => {
    if (designWidth.value <= 0 || designHeight.value <= 0) return 1
    if (size.width <= 0 || size.height <= 0) return 1
    return Math.min(size.width / designWidth.value, size.height / designHeight.value)
  }

  const stageScales: PreviewStageScaleMap = {
    title: computed(() => resolveStageScale(titleStageSize.value)),
    menu: computed(() => resolveStageScale(menuStageSize.value)),
    dialog: computed(() => resolveStageScale(dialogStageSize.value)),
    replay: computed(() => resolveStageScale(replayStageSize.value)),
    saveLoad: computed(() => resolveStageScale(saveLoadStageSize.value)),
    system: computed(() => resolveStageScale(systemStageSize.value)),
    payment: computed(() => resolveStageScale(paymentStageSize.value)),
  }

  const resizeHandlers: PreviewStageResizeHandlerMap = {
    title: (size) => {
      titleStageSize.value = size
    },
    menu: (size) => {
      menuStageSize.value = size
    },
    dialog: (size) => {
      dialogStageSize.value = size
    },
    replay: (size) => {
      replayStageSize.value = size
    },
    saveLoad: (size) => {
      saveLoadStageSize.value = size
    },
    system: (size) => {
      systemStageSize.value = size
    },
    payment: (size) => {
      paymentStageSize.value = size
    },
  }

  return {
    stageScales,
    resizeHandlers,
  }
}
